/**
 * Pane keyboard shortcuts — a pure chord table.
 *
 * Matching is on the PHYSICAL key, never on `e.key` alone, so the chords work
 * on the owner's German layout: there `=` is Shift+0 and Alt+Shift+"+" reports
 * `key: '*'`. For + and − the Windows virtual-key code (`keyCode` 187/189 =
 * VK_OEM_PLUS/VK_OEM_MINUS) is used — it is what Windows Terminal binds, and
 * it names the "+" key on a German keyboard and the "=" key on a US one.
 * Letters use `e.code` (KeyD, KeyW, KeyO), which is layout-independent for
 * these keys on QWERTY and QWERTZ alike.
 *
 * Windows/Linux mirror Windows Terminal; macOS mirrors iTerm2.
 */

const ARROWS = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };

const isPlus = (e) =>
  e.keyCode === 187 || e.keyCode === 107 || e.code === 'NumpadAdd' || (!e.keyCode && e.code === 'Equal');
const isMinus = (e) =>
  e.keyCode === 189 || e.keyCode === 109 || e.code === 'NumpadSubtract' || (!e.keyCode && e.code === 'Minus');

const mods = (e) => ({ c: !!e.ctrlKey, a: !!e.altKey, s: !!e.shiftKey, m: !!e.metaKey });
const only = (e, want) => {
  const m = mods(e);
  return m.c === !!want.c && m.a === !!want.a && m.s === !!want.s && m.m === !!want.m;
};

function matchWin(e) {
  const arrow = ARROWS[e.key] || ARROWS[e.code];
  if (only(e, { a: 1, s: 1 })) {
    if (isPlus(e)) return { type: 'split', dir: 'row' };
    if (isMinus(e)) return { type: 'split', dir: 'col' };
    if (arrow) return { type: 'resize', direction: arrow };
    if (e.code === 'KeyO') return { type: 'cycle', delta: 1 };
  }
  if (only(e, { a: 1 }) && arrow) return { type: 'focus', direction: arrow };
  if (only(e, { c: 1, s: 1 })) {
    if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') return { type: 'zoom' };
    if (e.code === 'KeyW') return { type: 'close' };
  }
  return null;
}

function matchMac(e) {
  const arrow = ARROWS[e.key] || ARROWS[e.code];
  if (only(e, { m: 1 }) && e.code === 'KeyD') return { type: 'split', dir: 'row' };
  if (only(e, { m: 1, s: 1 }) && e.code === 'KeyD') return { type: 'split', dir: 'col' };
  if (only(e, { m: 1, a: 1 }) && arrow) return { type: 'focus', direction: arrow };
  if (only(e, { m: 1, a: 1, s: 1 }) && arrow) return { type: 'resize', direction: arrow };
  if (only(e, { m: 1 }) && (e.code === 'BracketRight' || e.keyCode === 221)) return { type: 'cycle', delta: 1 };
  if (only(e, { m: 1 }) && (e.code === 'BracketLeft' || e.keyCode === 219)) return { type: 'cycle', delta: -1 };
  if (only(e, { m: 1, s: 1 }) && (e.key === 'Enter' || e.code === 'Enter')) return { type: 'zoom' };
  if (only(e, { m: 1, s: 1 }) && e.code === 'KeyW') return { type: 'close' };
  return null;
}

/** The pane action a keydown means, or null. `platform`: 'mac' | 'win'. */
export function matchPaneChord(e, platform = 'win') {
  if (!e || e.isComposing || e.type === 'keyup') return null;
  return platform === 'mac' ? matchMac(e) : matchWin(e);
}

/** Display key caps per action, for tooltips and Settings → Keyboard. */
export const PANE_CHORD_KEYS = {
  win: {
    splitRight: ['Alt', 'Shift', '+'],
    splitDown: ['Alt', 'Shift', '-'],
    focus: ['Alt', '←↑→↓'],
    resize: ['Alt', 'Shift', '←↑→↓'],
    cycle: ['Alt', 'Shift', 'O'],
    zoom: ['Ctrl', 'Shift', 'Enter'],
    close: ['Ctrl', 'Shift', 'W'],
  },
  mac: {
    splitRight: ['⌘', 'D'],
    splitDown: ['⌘', '⇧', 'D'],
    focus: ['⌥', '⌘', '←↑→↓'],
    resize: ['⌥', '⌘', '⇧', '←↑→↓'],
    cycle: ['⌘', '[ / ]'], // ⌘[ previous, ⌘] next — both work (card 50122def)
    zoom: ['⌘', '⇧', 'Enter'],
    close: ['⌘', '⇧', 'W'],
  },
};

export function currentPlatform() {
  if (typeof navigator === 'undefined') return 'win';
  const p = `${navigator.platform || ''} ${navigator.userAgent || ''}`;
  return /Mac|iPhone|iPad/i.test(p) ? 'mac' : 'win';
}

export const chordKeys = (action, platform = currentPlatform()) => PANE_CHORD_KEYS[platform][action] || [];
